import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  getPermissionsListForRole,
  enableAllPermissionsForRole,
  disableAllPermissionsForRole,
  saveAssignedPermissionsToRole,
} from "../../../services/team";
import { useAuth } from "../../../context/auth/AuthContext";
import NewPermissionModal from "./NewPermissionModal";
import { Button } from "../../Ui_Components/Button";

/* ── API response types ── */
interface ApiRoleOption {
  value: string;
  label: string;
}

interface ApiActionPermission {
  resource: string;
  action: string;
  label: string;
  enabled: boolean;
}

type ContentOpKey = "create" | "delete" | "edit" | "view";

interface ApiContentItem {
  resource: string;
  resource_label: string;
  operations: Record<ContentOpKey, { enabled: boolean }>;
}

interface RolePermissionsResponse {
  success: string;
  role: string;
  role_label: string;
  all_roles: ApiRoleOption[];
  total_count: number;
  enabled_count: number;
  actions: ApiActionPermission[];
  content: ApiContentItem[];
}

/* ── Local mutable state shape ── */
type ContentPerm = Record<ContentOpKey, boolean>;

interface PermState {
  actions: Record<string, boolean>; // key = `${resource}:${action}`
  content: Record<string, ContentPerm>; // key = resource
}

const CONTENT_OPS: ContentOpKey[] = ["create", "delete", "edit", "view"];
const CONTENT_OP_LABEL: Record<ContentOpKey, string> = {
  create: "Create",
  delete: "Delete",
  edit: "Edit",
  view: "View",
};

const actionKey = (resource: string, action: string) => `${resource}:${action}`;

/* ── Shimmer ── */
const shimmerStyles = `
  @keyframes shimmer {
    0%   { background-position: -1000px 0; }
    100% { background-position:  1000px 0; }
  }
  .ra-shimmer {
    animation: shimmer 2s infinite linear;
    background: linear-gradient(
      to right,
      #f0f0f0 0%,
      #e6e6e6 20%,
      #f0f0f0 40%,
      #f0f0f0 100%
    );
    background-size: 1000px 100%;
  }
`;

const PermissionsShimmer: React.FC = () => (
  <>
    <style>{shimmerStyles}</style>

    {/* Actions */}
    <div className="mb-8">
      <div className="h-3 w-20 rounded ra-shimmer mb-3" />
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
        {Array.from({ length: 12 }).map((_, i) => (
          <div
            key={i}
            className="flex items-center justify-between px-3 py-2.5 border border-[#E8E8E8] rounded-md"
          >
            <div className="h-3 w-32 rounded ra-shimmer" />
            <div className="h-5 w-9 rounded-full ra-shimmer" />
          </div>
        ))}
      </div>
    </div>

    {/* Manage content */}
    <div>
      <div className="flex items-center justify-between mb-3">
        <div className="h-3 w-32 rounded ra-shimmer" />
        <div className="h-3 w-48 rounded ra-shimmer" />
      </div>
      <div className="border border-[#F3F4F6] rounded-md overflow-hidden">
        {Array.from({ length: 5 }).map((_, idx, arr) => (
          <div
            key={idx}
            className={`flex items-center justify-between px-4 py-3 ${
              idx < arr.length - 1 ? "border-b border-[#F3F4F6]" : ""
            }`}
          >
            <div className="h-3 w-28 rounded ra-shimmer" />
            <div className="flex items-center gap-6">
              {Array.from({ length: 4 }).map((__, j) => (
                <div key={j} className="flex items-center gap-1.5">
                  <div className="h-4 w-4 rounded ra-shimmer" />
                  <div className="h-3 w-12 rounded ra-shimmer" />
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  </>
);

/* ── Toggle switch ── */
const Toggle: React.FC<{ checked: boolean; onChange: () => void }> = ({
  checked, onChange,
}) => (
  <button
    type="button"
    onClick={onChange}
    className={`relative inline-flex h-5 w-9 flex-shrink-0 items-center rounded-full transition-colors focus:outline-none ${
      checked ? "bg-[#0DB5B5]" : "bg-[#D1D5DB]"
    }`}
  >
    <span
      className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform ${
        checked ? "translate-x-[18px]" : "translate-x-1"
      }`}
    />
  </button>
);

/* ── Checkbox ── */
const Checkbox: React.FC<{ checked: boolean; onChange: () => void; label: string }> = ({
  checked, onChange, label,
}) => (
  <label className="flex items-center gap-1.5 cursor-pointer select-none">
    <button
      type="button"
      role="checkbox"
      aria-checked={checked}
      onClick={onChange}
      className={`w-4 h-4 rounded flex items-center justify-center flex-shrink-0 border transition-colors ${
        checked ? "bg-[#0DB5B5] border-[#0DB5B5]" : "bg-white border-[#D1D5DB]"
      }`}
    >
      {checked && (
        <svg width="10" height="8" viewBox="0 0 10 8" fill="none">
          <path d="M1 4L3.5 6.5L9 1" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      )}
    </button>
    <span className="text-xs 3xl:text-sm font-inter text-[#1F1F1F]">{label}</span>
  </label>
);

/* ── Main page ── */
const RolesAccessPage: React.FC = () => {
  const navigate = useNavigate();
  const { user, refreshProfile } = useAuth();

  const [roles, setRoles] = useState<ApiRoleOption[]>([]);
  const [activeRole, setActiveRole] = useState<string>("");
  const [activeRoleLabel, setActiveRoleLabel] = useState<string>("");
  const [actionDefs, setActionDefs] = useState<ApiActionPermission[]>([]);
  const [contentDefs, setContentDefs] = useState<ApiContentItem[]>([]);
  const [permissions, setPermissions] = useState<PermState>({ actions: {}, content: {} });
  const [totalCount, setTotalCount] = useState(0);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [bulkAction, setBulkAction] = useState<"enable" | "disable" | null>(null);
  const [newPermissionOpen, setNewPermissionOpen] = useState(false);

  const applyRoleData = useCallback((data: RolePermissionsResponse) => {
    if (data.all_roles?.length) setRoles(data.all_roles);
    setActiveRole(data.role);
    setActiveRoleLabel(data.role_label);
    setActionDefs(data.actions ?? []);
    setContentDefs(data.content ?? []);
    setTotalCount(data.total_count ?? 0);

    const actionsState: Record<string, boolean> = {};
    (data.actions ?? []).forEach((a) => {
      actionsState[actionKey(a.resource, a.action)] = !!a.enabled;
    });

    const contentState: Record<string, ContentPerm> = {};
    (data.content ?? []).forEach((c) => {
      contentState[c.resource] = {
        create: !!c.operations?.create?.enabled,
        delete: !!c.operations?.delete?.enabled,
        edit:   !!c.operations?.edit?.enabled,
        view:   !!c.operations?.view?.enabled,
      };
    });
    setPermissions({ actions: actionsState, content: contentState });
  }, []);

  const loadRolePermissions = useCallback(
    async (role: string): Promise<RolePermissionsResponse | null> => {
      setLoading(true);
      setError(null);
      try {
        const data: RolePermissionsResponse = await getPermissionsListForRole(role);
        if (data.success !== "1") {
          throw new Error("Failed to load role permissions");
        }
        applyRoleData(data);
        return data;
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to load role permissions");
        return null;
      } finally {
        setLoading(false);
      }
    },
    [applyRoleData],
  );

  // Initial load — probe to discover `all_roles`, then load the first role.
  // We intentionally DO NOT apply the probe's permission data to state when it
  // isn't already the first role; otherwise the page would briefly paint that
  // role's permissions before swapping to the correct one. Loading stays true
  // through the follow-up fetch so the spinner shows continuously.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const probe: RolePermissionsResponse = await getPermissionsListForRole("Head nutritionist");
        if (cancelled) return;
        if (probe.success !== "1") {
          throw new Error("Failed to load role permissions");
        }
        const firstRole = probe.all_roles?.[0]?.value;
        if (!firstRole || firstRole === probe.role) {
          applyRoleData(probe);
          setLoading(false);
        } else {
          // Render tabs + highlight the correct active tab while we fetch its
          // data; permission body stays behind the spinner until that arrives.
          setRoles(probe.all_roles);
          setActiveRole(firstRole);
          await loadRolePermissions(firstRole);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : "Failed to load role permissions");
          setLoading(false);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [applyRoleData, loadRolePermissions]);

  const handleRoleChange = (role: string) => {
    if (role === activeRole) return;
    loadRolePermissions(role);
  };

  const enabledCount = useMemo(() => {
    const actionCount = Object.values(permissions.actions).filter(Boolean).length;
    const contentCount = Object.values(permissions.content).reduce(
      (sum, c) => sum + CONTENT_OPS.reduce((s, op) => s + (c[op] ? 1 : 0), 0),
      0,
    );
    return actionCount + contentCount;
  }, [permissions]);

  const applyBulkState = (enabled: boolean) => {
    const actions: Record<string, boolean> = {};
    actionDefs.forEach((a) => { actions[actionKey(a.resource, a.action)] = enabled; });
    const content: Record<string, ContentPerm> = {};
    contentDefs.forEach((c) => {
      content[c.resource] = { create: enabled, delete: enabled, edit: enabled, view: enabled };
    });
    setPermissions({ actions, content });
  };

  const runBulkAction = async (mode: "enable" | "disable") => {
    if (!activeRole) return;
    if (!user?.nutrist_id) {
      setSaveError("Missing caller identity. Please sign in again.");
      return;
    }
    setBulkAction(mode);
    setSaveError(null);
    try {
      const payload = { caller_id: user.nutrist_id };
      const data =
        mode === "enable"
          ? await enableAllPermissionsForRole(activeRole, payload)
          : await disableAllPermissionsForRole(activeRole, payload);
      if (data.success !== "1") {
        throw new Error(data.message || `${mode} all failed`);
      }
      applyBulkState(mode === "enable");
      if (typeof data.total_count === "number") setTotalCount(data.total_count);
      // Bulk toggle may have changed the signed-in user's effective perms.
      refreshProfile();
    } catch (err) {
      setSaveError(
        err instanceof Error
          ? err.message
          : `Failed to ${mode === "enable" ? "enable" : "disable"} all permissions`,
      );
    } finally {
      setBulkAction(null);
    }
  };

  const enableAll = () => runBulkAction("enable");
  const disableAll = () => runBulkAction("disable");

  const toggleAction = (resource: string, action: string) => {
    const key = actionKey(resource, action);
    setPermissions((prev) => ({
      ...prev,
      actions: { ...prev.actions, [key]: !prev.actions[key] },
    }));
  };

  const toggleContent = (resource: string, op: ContentOpKey) => {
    setPermissions((prev) => {
      const current = prev.content[resource] ?? { create: false, delete: false, edit: false, view: false };
      return {
        ...prev,
        content: { ...prev.content, [resource]: { ...current, [op]: !current[op] } },
      };
    });
  };

  const handleSave = async () => {
    if (!activeRole) return;
    if (!user?.nutrist_id) {
      setSaveError("Missing caller identity. Please sign in again.");
      return;
    }

    // Server expects the FULL set of currently enabled permissions; it computes
    // added/removed itself and returns them in the response.
    const selected: { resource: string; action: string }[] = [];
    Object.entries(permissions.actions).forEach(([key, enabled]) => {
      if (!enabled) return;
      const [resource, action] = key.split(":");
      selected.push({ resource, action });
    });
    Object.entries(permissions.content).forEach(([resource, ops]) => {
      CONTENT_OPS.forEach((op) => {
        if (ops[op]) selected.push({ resource, action: op });
      });
    });

    setSaving(true);
    setSaveError(null);
    try {
      const data = await saveAssignedPermissionsToRole(activeRole, {
        caller_id: user.nutrist_id,
        permissions: selected,
      });
      if (data.success !== "1") {
        throw new Error(data.message || `Save failed`);
      }
      // Saving this role may have changed the signed-in user's effective
      // perms (if they hold this role). Refresh the auth context so gated
      // UI on the next page reflects the new permission set.
      await refreshProfile();
      navigate("/team");
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : "Failed to save permissions");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="h-screen flex flex-col bg-[#F7F7FA]">

      {/* ── Header ── */}
      <div className="flex-shrink-0 bg-white border-b border-[#E8E8E8] px-4 sm:px-6 lg:px-8 py-4">
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-xl 3xl:text-2xl font-semibold font-inter text-[#1F1F1F]">
              Roles &amp; access
            </h1>
            <nav className="flex items-center gap-1 text-xs 3xl:text-sm font-inter text-[#646464] mt-0.5">
              <button
                onClick={() => navigate("/team")}
                className="hover:text-[#0DB5B5] transition-colors"
              >
                Team
              </button>
              <span>›</span>
              <span>Roles &amp; access</span>
            </nav>
          </div>
          <Button
            variant="primary"
            onClick={() => setNewPermissionOpen(true)}
          >
            New permission
          </Button>
        </div>
      </div>

      {/* ── Role tabs ── */}
      <div className="flex-shrink-0 bg-white border-b border-[#F3F4F6] px-4 sm:px-6 lg:px-8 py-3">
        <div className="flex items-center gap-2 flex-wrap">
          {roles.length === 0 && loading ? (
            <>
              <style>{shimmerStyles}</style>
              {Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="h-7 w-32 rounded-full ra-shimmer" />
              ))}
            </>
          ) : (
            roles.map((role) => (
              <button
                key={role.value}
                type="button"
                onClick={() => handleRoleChange(role.value)}
                disabled={loading && role.value !== activeRole}
                className={`px-4 py-1.5 rounded-full text-sm 3xl:text-base font-medium font-inter border transition-colors ${
                  activeRole === role.value
                    ? "bg-[#1F1F1F] text-white border-[#1F1F1F]"
                    : "bg-white text-[#1F1F1F] border-[#D1D5DB] hover:border-[#1F1F1F]"
                } disabled:opacity-60 disabled:cursor-not-allowed`}
              >
                {role.label}
              </button>
            ))
          )}
        </div>
      </div>

      {/* ── Scrollable content ── */}
      <div className="flex-1 overflow-y-auto px-4 sm:px-6 lg:px-8 py-4 custom-scrollbar-overlay">
        <div className="bg-white rounded-[6px] border border-[#F3F4F6] shadow-sm p-6">

          {/* Section header */}
          <div className="flex items-start justify-between mb-6">
            <div>
              <h2 className="text-lg 3xl:text-xl font-semibold font-inter text-[#1F1F1F]">
                {activeRoleLabel || "—"}
              </h2>
              <p className="text-xs 3xl:text-sm font-inter text-[#646464] mt-0.5">
                {enabledCount} of {totalCount} enabled permissions
              </p>
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={enableAll}
                disabled={loading || bulkAction !== null || saving}
                className="px-4 py-1.5 border border-[#0DB5B5] text-[#0DB5B5] rounded-[6px] text-sm 3xl:text-base font-medium font-inter hover:bg-[#E5F8F8] transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {bulkAction === "enable" ? "Enabling..." : "Enable all"}
              </button>
              <button
                type="button"
                onClick={disableAll}
                disabled={loading || bulkAction !== null || saving}
                className="px-4 py-1.5 border border-red-400 text-red-500 rounded-[6px] text-sm 3xl:text-base font-medium font-inter hover:bg-red-50 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {bulkAction === "disable" ? "Disabling..." : "Disable all"}
              </button>
            </div>
          </div>

          {error && (
            <p className="mb-4 text-sm font-inter text-red-500">{error}</p>
          )}

          {loading ? (
            <PermissionsShimmer />
          ) : (
            <>
              {/* ── Actions ── */}
              <div className="mb-8">
                <p className="text-xs 3xl:text-sm font-semibold font-inter text-[#646464] uppercase tracking-wide mb-3">
                  Actions
                </p>
                {actionDefs.length === 0 ? (
                  <p className="text-sm font-inter text-gray-400">No actions for this role</p>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                    {actionDefs.map((action) => {
                      const key = actionKey(action.resource, action.action);
                      return (
                        <div
                          key={key}
                          className="flex items-center justify-between px-3 py-2.5 border border-[#E8E8E8] rounded-md"
                        >
                          <span className="text-sm 3xl:text-base font-inter text-[#1F1F1F]">
                            {action.label}
                          </span>
                          <Toggle
                            checked={!!permissions.actions[key]}
                            onChange={() => toggleAction(action.resource, action.action)}
                          />
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* ── Manage content ── */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <p className="text-xs 3xl:text-sm font-semibold font-inter text-[#646464] uppercase tracking-wide">
                    Manage content
                  </p>
                  <span className="text-xs 3xl:text-sm font-inter text-[#828282]">
                    Toggle create / delete / edit / view
                  </span>
                </div>

                {contentDefs.length === 0 ? (
                  <p className="text-sm font-inter text-gray-400">No content permissions for this role</p>
                ) : (
                  <div className="border border-[#F3F4F6] rounded-md overflow-hidden">
                    {contentDefs.map((item, idx) => {
                      const c = permissions.content[item.resource] ?? {
                        create: false, delete: false, edit: false, view: false,
                      };
                      return (
                        <div
                          key={item.resource}
                          className={`flex items-center justify-between px-4 py-3 ${
                            idx < contentDefs.length - 1 ? "border-b border-[#F3F4F6]" : ""
                          }`}
                        >
                          <span className="text-sm 3xl:text-base font-inter text-[#1F1F1F]">
                            {item.resource_label}
                          </span>
                          <div className="flex items-center gap-6">
                            {CONTENT_OPS.map((op) => (
                              <Checkbox
                                key={op}
                                checked={c[op]}
                                onChange={() => toggleContent(item.resource, op)}
                                label={CONTENT_OP_LABEL[op]}
                              />
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </>
          )}

        </div>
      </div>

      {/* ── Sticky footer ── */}
      <div className="flex-shrink-0 bg-white border-t border-[#E8E8E8] px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-end gap-3">
        {saveError && (
          <span className="text-sm font-inter text-red-500 mr-auto">{saveError}</span>
        )}
        <Button
          variant="secondary"
          size="md"
          onClick={() => navigate("/team")}
          disabled={saving}
        >
          Cancel
        </Button>
        <Button
          variant="primary"
          size="md"
          onClick={handleSave}
          disabled={saving || loading || !activeRole}
        >
          {saving ? "Saving..." : "Save changes"}
        </Button>
      </div>

      {newPermissionOpen && (
        <NewPermissionModal
          onClose={() => setNewPermissionOpen(false)}
          onAdded={() => {
            setNewPermissionOpen(false);
            if (activeRole) loadRolePermissions(activeRole);
          }}
        />
      )}

    </div>
  );
};

export default RolesAccessPage;
